import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePersistedState } from '@/hooks/usePersistedState';
import { useToastStore } from '@/store/toastStore';
import { getUnitShort } from '@/constants/measurementUnits';
import {
  Box,
  Typography,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  LocalFlorist,
  Science,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import {
  Button,
  Card,
  InputField,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  ConfirmDialog,
  PageHeader,
  FiltersCard,
  AllergenChip,
} from '@/components/ui';
import { ingredientService } from '@/services/ingredient.service';
import { allergenService } from '@/services/allergen.service';
import type { IngredientCategory, IngredientFilters } from '@/types';

/**
 * Page de gestion des ingrédients
 * Liste avec recherche, filtres et actions CRUD
 */
export function IngredientsPage(): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = usePersistedState('ingredients.searchQuery', '');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = usePersistedState<IngredientCategory | 'all'>('ingredients.categoryFilter', 'all');
  const [vegetarianFilter, setVegetarianFilter] = usePersistedState<'all' | 'vegetarian' | 'non-vegetarian'>('ingredients.vegetarianFilter', 'all');
  const [veganFilter, setVeganFilter] = usePersistedState<'all' | 'vegan' | 'non-vegan'>('ingredients.veganFilter', 'all');
  const [allergenFilter, setAllergenFilter] = usePersistedState<string>('ingredients.allergenFilter', 'all');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // État pour le dialog de confirmation de suppression
  const [deleteConfirm, setDeleteConfirm] = useState<{
    open: boolean;
    ingredientId: string | null;
    ingredientName: string;
  }>({
    open: false,
    ingredientId: null,
    ingredientName: '',
  });

  const filters: IngredientFilters = {
    search: debouncedSearch.trim() || undefined,
    category: categoryFilter !== 'all' ? categoryFilter : undefined,
    isVegetarian:
      vegetarianFilter === 'all'
        ? undefined
        : vegetarianFilter === 'vegetarian',
    isVegan: veganFilter === 'all' ? undefined : veganFilter === 'vegan',
    allergenId: allergenFilter !== 'all' ? allergenFilter : undefined,
  };

  const {
    data: ingredients = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['ingredients', filters],
    queryFn: () => ingredientService.getAll(filters),
  });

  // Récupérer les allergènes pour le filtre
  const { data: allergens = [] } = useQuery({
    queryKey: ['allergens'],
    queryFn: () => allergenService.getAll(),
  });

  // Store pour les toasts
  const { success, error: showError } = useToastStore();

  // Mutation pour supprimer un ingrédient
  const deleteMutation = useMutation({
    mutationFn: (id: string) => ingredientService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ingredients'] });
      success('Ingrédient supprimé avec succès');
      setDeleteConfirm({ open: false, ingredientId: null, ingredientName: '' });
    },
    onError: (err: any) => {
      showError(
        err?.response?.data?.message ||
          "Erreur lors de la suppression de l'ingrédient"
      );
    },
  });

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteConfirm({ open: true, ingredientId: id, ingredientName: name });
  };

  const handleDeleteConfirm = () => {
    if (deleteConfirm.ingredientId) {
      deleteMutation.mutate(deleteConfirm.ingredientId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ open: false, ingredientId: null, ingredientName: '' });
  };

  // Formater la catégorie
  const formatCategory = (category: IngredientCategory): string => {
    const categories: Record<IngredientCategory, string> = {
      MEAT: 'Viande',
      FISH: 'Poisson',
      VEGETABLE: 'Légume',
      STARCH: 'Féculent',
      DAIRY: 'Produit laitier',
      SPICE: 'Épice',
      FRUIT: 'Fruit',
      OTHER: 'Autre',
    };
    return categories[category] || category;
  };

  // Couleur de la catégorie
  const getCategoryColor = (category: IngredientCategory): string => {
    const colors: Record<IngredientCategory, string> = {
      MEAT: '#e74c3c',
      FISH: '#3498db',
      VEGETABLE: '#27ae60',
      STARCH: '#f39c12',
      DAIRY: '#ecf0f1',
      SPICE: '#e67e22',
      FRUIT: '#9b59b6',
      OTHER: '#95a5a6',
    };
    return colors[category] || '#95a5a6';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <PageHeader
        title="Ingrédients"
        subtitle="Gérez la composition et les allergènes de vos produits"
        action={
          <Button
            variant="primary"
            startIcon={<Add />}
            onClick={() => navigate('/ingredients/new')}
          >
            Nouvel ingrédient
          </Button>
        }
      />

      <FiltersCard>
        <InputField
          placeholder="Rechercher un ingrédient..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          icon={<Search />}
          iconPosition="start"
          sx={{ flexGrow: 1, minWidth: 250, '& .MuiOutlinedInput-root': { height: 40 } }}
        />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            flex: { lg: '0 0 auto' },
          }}
        >
            {/* Filtre Catégorie */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Catégorie</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as any)}
                label="Catégorie"
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="MEAT">Viande</MenuItem>
                <MenuItem value="FISH">Poisson</MenuItem>
                <MenuItem value="VEGETABLE">Légume</MenuItem>
                <MenuItem value="STARCH">Féculent</MenuItem>
                <MenuItem value="DAIRY">Produit laitier</MenuItem>
                <MenuItem value="SPICE">Épice</MenuItem>
                <MenuItem value="FRUIT">Fruit</MenuItem>
                <MenuItem value="OTHER">Autre</MenuItem>
              </Select>
            </FormControl>

            {/* Filtre Végétarien */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Végétarien</InputLabel>
              <Select
                value={vegetarianFilter}
                onChange={(e) => setVegetarianFilter(e.target.value as any)}
                label="Végétarien"
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="vegetarian">Oui</MenuItem>
                <MenuItem value="non-vegetarian">Non</MenuItem>
              </Select>
            </FormControl>

            {/* Filtre Vegan */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Vegan</InputLabel>
              <Select
                value={veganFilter}
                onChange={(e) => setVeganFilter(e.target.value as any)}
                label="Vegan"
              >
                <MenuItem value="all">Tous</MenuItem>
                <MenuItem value="vegan">Oui</MenuItem>
                <MenuItem value="non-vegan">Non</MenuItem>
              </Select>
            </FormControl>

            {/* Filtre Allergène */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Allergène</InputLabel>
              <Select
                value={allergenFilter}
                onChange={(e) => setAllergenFilter(e.target.value)}
                label="Allergène"
              >
                <MenuItem value="all">Tous</MenuItem>
                {allergens.map((allergen) => (
                  <MenuItem key={allergen.id} value={allergen.id}>
                    {allergen.icon} {allergen.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
        </Box>
      </FiltersCard>

      {/* Tableau des ingrédients */}
      {isLoading ? (
        <LoadingSpinner />
      ) : queryError ? (
        <ErrorMessage
          message="Erreur lors du chargement des ingrédients"
          onRetry={() => queryClient.invalidateQueries({ queryKey: ['ingredients'] })}
        />
      ) : ingredients.length === 0 ? (
        <EmptyState
          icon={<Science sx={{ fontSize: 64 }} />}
          title="Aucun ingrédient trouvé"
          description={
            Object.values(filters).some((v) => v !== undefined)
              ? 'Essayez de modifier vos critères de recherche'
              : 'Commencez par créer votre premier ingrédient'
          }
          action={
            !Object.values(filters).some((v) => v !== undefined)
              ? { label: 'Créer un ingrédient', onClick: () => navigate('/ingredients/new'), variant: 'primary' as const }
              : undefined
          }
        />
      ) : (
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.paper' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Nom</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Catégorie</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Allergènes</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Végétarien
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Vegan
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Produits
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ingredients.map((ingredient) => (
                  <TableRow
                    key={ingredient.id}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/ingredients/${ingredient.id}/edit`)}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: 'text.primary' }}
                      >
                        {ingredient.name}
                      </Typography>
                      {ingredient.unit && (
                        <Typography variant="caption" color="text.secondary">
                          Unité: {getUnitShort(ingredient.unit)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={formatCategory(ingredient.category)}
                        size="small"
                        sx={{
                          backgroundColor: getCategoryColor(ingredient.category),
                          color:
                            ingredient.category === 'DAIRY'
                              ? 'text.primary'
                              : '#fff',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {ingredient.allergens.length > 0 ? (
                          ingredient.allergens.map((ia) => (
                            <AllergenChip
                              key={ia.allergen.id}
                              label={
                                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span>{ia.allergen.icon}</span>
                                  <span>{ia.allergen.code}</span>
                                </Box>
                              }
                            />
                          ))
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Aucun
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {ingredient.isVegetarian ? (
                        <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <Cancel sx={{ color: 'error.main', fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {ingredient.isVegan ? (
                        <LocalFlorist sx={{ color: 'success.main', fontSize: 20 }} />
                      ) : (
                        <Cancel sx={{ color: 'error.main', fontSize: 20 }} />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ingredient._count?.products || 0}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/ingredients/${ingredient.id}/edit`);
                          }}
                          sx={{
                            color: 'primary.main',
                            '&:hover': { backgroundColor: 'primary.lighter' },
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(ingredient.id, ingredient.name);
                          }}
                          sx={{
                            color: 'error.main',
                            '&:hover': { backgroundColor: 'error.lighter' },
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Dialog de confirmation de suppression */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title="Supprimer l'ingrédient"
        message={`Êtes-vous sûr de vouloir supprimer "${deleteConfirm.ingredientName}" ? Cette action est irréversible.`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Supprimer"
        cancelText="Annuler"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
